import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Storefront, Gift, CurrencyDollar, SignOut } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [merchants, setMerchants] = useState([]);
  const [giftCards, setGiftCards] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [activeTab, setActiveTab] = useState('merchants');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'merchants') {
        const { data } = await axios.get(`${API_URL}/api/admin/merchants`, {
          withCredentials: true
        });
        setMerchants(data.merchants);
      } else if (activeTab === 'giftcards') {
        const { data } = await axios.get(`${API_URL}/api/admin/gift-cards`, {
          withCredentials: true
        });
        setGiftCards(data.gift_cards);
      } else if (activeTab === 'settlements') {
        const { data } = await axios.get(`${API_URL}/api/admin/settlements`, {
          withCredentials: true
        });
        setSettlements(data.settlements);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#18261E]">
      {/* Header */}
      <div className="bg-[#283D32]/80 backdrop-blur-xl border-b border-[#3E5248] px-6 py-6">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div>
            <div className="brand-logo text-2xl text-gray-100" data-testid="brand-logo">habitué</div>
            <h1 className="text-2xl tracking-tight text-white mt-2" data-testid="admin-title">Admin Dashboard</h1>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-[#889990] hover:text-white transition-colors"
            data-testid="logout-button"
          >
            <SignOut size={20} />
            <span className="text-sm">Logout</span>
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-[#3E5248]">
          <button
            onClick={() => setActiveTab('merchants')}
            className={`flex items-center gap-2 pb-4 px-4 transition-colors ${
              activeTab === 'merchants'
                ? 'text-white border-b-2 border-gray-300'
                : 'text-[#889990] hover:text-gray-200'
            }`}
            data-testid="tab-merchants"
          >
            <Storefront size={20} />
            <span className="font-medium">Merchants</span>
          </button>
          <button
            onClick={() => setActiveTab('giftcards')}
            className={`flex items-center gap-2 pb-4 px-4 transition-colors ${
              activeTab === 'giftcards'
                ? 'text-white border-b-2 border-gray-300'
                : 'text-[#889990] hover:text-gray-200'
            }`}
            data-testid="tab-giftcards"
          >
            <Gift size={20} />
            <span className="font-medium">Gift Cards</span>
          </button>
          <button
            onClick={() => setActiveTab('settlements')}
            className={`flex items-center gap-2 pb-4 px-4 transition-colors ${
              activeTab === 'settlements'
                ? 'text-white border-b-2 border-gray-300'
                : 'text-[#889990] hover:text-gray-200'
            }`}
            data-testid="tab-settlements"
          >
            <CurrencyDollar size={20} />
            <span className="font-medium">Settlements</span>
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-gray-300 border-r-transparent"></div>
            <p className="mt-4 text-[#889990]">Loading...</p>
          </div>
        ) : (
          <>
            {/* Merchants Tab */}
            {activeTab === 'merchants' && (
              <div className="bg-[#283D32]/50 backdrop-blur-sm border border-[#3E5248] rounded-2xl overflow-hidden" data-testid="merchants-table">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#283D32] border-b border-[#3E5248]">
                      <tr>
                        <th className="text-left px-6 py-4 text-xs tracking-[0.2em] uppercase text-[#889990] font-medium">Business</th>
                        <th className="text-left px-6 py-4 text-xs tracking-[0.2em] uppercase text-[#889990] font-medium">Type</th>
                        <th className="text-left px-6 py-4 text-xs tracking-[0.2em] uppercase text-[#889990] font-medium">Owner</th>
                        <th className="text-left px-6 py-4 text-xs tracking-[0.2em] uppercase text-[#889990] font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {merchants.map((merchant, index) => (
                        <tr key={merchant.id} className="hover:bg-[#344840]/50 transition-colors" data-testid={`merchant-row-${index}`}>
                          <td className="px-6 py-4 text-white">{merchant.business_name}</td>
                          <td className="px-6 py-4 text-[#889990]">{merchant.business_type}</td>
                          <td className="px-6 py-4">
                            <div className="text-white">{merchant.owner_name}</div>
                            <div className="text-sm text-[#889990]">{merchant.owner_email}</div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                              merchant.status === 'active'
                                ? 'bg-[#283D32]/50 text-[#889990] border border-[#3E5248]'
                                : 'bg-[#344840]/50 text-[#E8EBE9] border border-[#3E5248]'
                            }`}>
                              {merchant.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {merchants.length === 0 && (
                    <div className="text-center py-12 text-[#889990]" data-testid="no-merchants">
                      No merchants yet
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Gift Cards Tab */}
            {activeTab === 'giftcards' && (
              <div className="bg-[#283D32]/50 backdrop-blur-sm border border-[#3E5248] rounded-2xl overflow-hidden" data-testid="giftcards-table">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#283D32] border-b border-[#3E5248]">
                      <tr>
                        <th className="text-left px-6 py-4 text-xs tracking-[0.2em] uppercase text-[#889990] font-medium">Code</th>
                        <th className="text-left px-6 py-4 text-xs tracking-[0.2em] uppercase text-[#889990] font-medium">Amount</th>
                        <th className="text-left px-6 py-4 text-xs tracking-[0.2em] uppercase text-[#889990] font-medium">Balance</th>
                        <th className="text-left px-6 py-4 text-xs tracking-[0.2em] uppercase text-[#889990] font-medium">Status</th>
                        <th className="text-left px-6 py-4 text-xs tracking-[0.2em] uppercase text-[#889990] font-medium">Sender</th>
                        <th className="text-left px-6 py-4 text-xs tracking-[0.2em] uppercase text-[#889990] font-medium">Receiver</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {giftCards.map((card, index) => (
                        <tr key={card.id} className="hover:bg-[#344840]/50 transition-colors" data-testid={`giftcard-row-${index}`}>
                          <td className="px-6 py-4 text-gray-200 font-mono text-sm">{card.code}</td>
                          <td className="px-6 py-4 text-white">£{card.amount.toFixed(2)}</td>
                          <td className="px-6 py-4 text-gray-100 font-medium">£{card.balance.toFixed(2)}</td>
                          <td className="px-6 py-4">
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                              card.status === 'active'
                                ? 'bg-[#283D32]/50 text-[#889990] border border-[#3E5248]'
                                : card.status === 'pending'
                                ? 'bg-yellow-900/50 text-yellow-300 border border-yellow-700'
                                : 'bg-[#344840]/50 text-[#E8EBE9] border border-[#3E5248]'
                            }`}>
                              {card.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-[#889990]">{card.sender_name}</td>
                          <td className="px-6 py-4 text-[#889990]">{card.receiver_email}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {giftCards.length === 0 && (
                    <div className="text-center py-12 text-[#889990]" data-testid="no-giftcards">
                      No gift cards yet
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Settlements Tab */}
            {activeTab === 'settlements' && (
              <div className="bg-[#283D32]/50 backdrop-blur-sm border border-[#3E5248] rounded-2xl overflow-hidden" data-testid="settlements-table">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#283D32] border-b border-[#3E5248]">
                      <tr>
                        <th className="text-left px-6 py-4 text-xs tracking-[0.2em] uppercase text-[#889990] font-medium">Merchant</th>
                        <th className="text-left px-6 py-4 text-xs tracking-[0.2em] uppercase text-[#889990] font-medium">Period</th>
                        <th className="text-left px-6 py-4 text-xs tracking-[0.2em] uppercase text-[#889990] font-medium">Revenue</th>
                        <th className="text-left px-6 py-4 text-xs tracking-[0.2em] uppercase text-[#889990] font-medium">Fees</th>
                        <th className="text-left px-6 py-4 text-xs tracking-[0.2em] uppercase text-[#889990] font-medium">Net Amount</th>
                        <th className="text-left px-6 py-4 text-xs tracking-[0.2em] uppercase text-[#889990] font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {settlements.map((settlement, index) => (
                        <tr key={settlement.id} className="hover:bg-[#344840]/50 transition-colors" data-testid={`settlement-row-${index}`}>
                          <td className="px-6 py-4 text-white">{settlement.merchant_name}</td>
                          <td className="px-6 py-4 text-[#889990]">{settlement.period}</td>
                          <td className="px-6 py-4 text-white">£{settlement.total_revenue.toFixed(2)}</td>
                          <td className="px-6 py-4 text-[#889990]">£{settlement.fees.toFixed(2)}</td>
                          <td className="px-6 py-4 text-gray-100 font-medium">£{settlement.net_amount.toFixed(2)}</td>
                          <td className="px-6 py-4">
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                              settlement.status === 'completed'
                                ? 'bg-[#283D32]/50 text-[#889990] border border-[#3E5248]'
                                : 'bg-yellow-900/50 text-yellow-300 border border-yellow-700'
                            }`}>
                              {settlement.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {settlements.length === 0 && (
                    <div className="text-center py-12 text-[#889990]" data-testid="no-settlements">
                      No settlements yet
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
